/**********************************************************************
 * src/components/overlays/PersonOverlay.jsx
 *********************************************************************/
import React, { useState, useEffect, useMemo } from 'react'
import Select                      from 'react-select'
import api                         from '../../../services/api'
import { formatImageBytes, normalizePersonImage } from '../../../utils/normalizePersonImage'
import '../../../css/personOverlay.css'

const PersonOverlay = ({ person, onClose, onUpdate, qualifications }) => {
  /* ---------- today (YYYY-MM-DD) per il max dell’input ------------- */
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), [])

  /* ---------- form state ------------------------------------------ */
  const [formData, setFormData] = useState({
    nome           : '',
    cognome        : '',
    data_nascita   : '',
    sesso          : '',
    description    : '',
    phone          : '',
    email          : '',
    imageBase64    : '',
    qualifications : [],
  })

  const [preview,  setPreview]  = useState(null)
  const [error,    setError]    = useState('')
  const [success,  setSuccess]  = useState('')
  const [imageInfo, setImageInfo] = useState(null)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  /* ---------- preload dati persona -------------------------------- */
  useEffect(() => {
    if (person) {
      setFormData({
        nome           : person.nome        || '',
        cognome        : person.cognome     || '',
        data_nascita   : person.dataNascita
                          ? person.dataNascita.slice(0, 10)
                          : '',
        sesso          : person.sesso       || '',
        description    : person.description || '',
        phone          : person.phone       || '',
        email          : person.email       || '',
        imageBase64    : '',
        qualifications : person.qualifications
          ? person.qualifications.map(q => q.id)
          : [],
      })
    }
  }, [person])

  /* ---------- handlers -------------------------------------------- */
  const handleChange = e =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const handleFileChange = async e => {
    const file = e.target.files[0]
    if (!file) {
      setFormData(prev => ({ ...prev, imageBase64: '' }))
      setPreview(null)
      setImageInfo(null)
      return
    }

    setError('')
    setSuccess('')
    setIsImageLoading(true)
    try {
      const normalized = await normalizePersonImage(file)
      setFormData(prev => ({ ...prev, imageBase64: normalized.dataUrl }))
      setPreview(normalized.dataUrl)
      setImageInfo(normalized)
    } catch (err) {
      console.error('Errore nella normalizzazione della foto:', err)
      setFormData(prev => ({ ...prev, imageBase64: '' }))
      setPreview(null)
      setImageInfo(null)
      setError(err?.message || 'Impossibile preparare questa immagine.')
      e.target.value = ''
    } finally {
      setIsImageLoading(false)
    }
  }

  const qualificationOptions = useMemo(
    () =>
      qualifications.map(q => ({ value: q.id, label: q.name })),
    [qualifications]
  )

  const handleQualificationSelect = sel =>
    setFormData(prev => ({
      ...prev,
      qualifications: Array.isArray(sel) ? sel.map(o => o.value) : [],
    }))

  /* ---------- submit ---------------------------------------------- */
  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (isImageLoading || isSubmitting) return

    /* check DOB non futura ---------------------------------------- */
    if (formData.data_nascita && formData.data_nascita > todayStr) {
      setError('La data di nascita non può essere nel futuro.')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = { ...formData }
      const response = await api.put(
        `/people/${person.id}`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 45000,
        }
      )
      const updatedPerson = response.data?.data || response.data

      /* bust cache immagine se serve */
      if (updatedPerson.image_url) {
        const base = updatedPerson.image_url.split('?')[0]
        updatedPerson.image_url = `${base}?t=${Date.now()}`
      }

      setSuccess('Persona aggiornata con successo!')
      onUpdate?.()
      setTimeout(onClose, 1500)
    } catch (err) {
      console.error("Errore nell'aggiornamento della persona:", err)
      const message = err?.code === 'ECONNABORTED'
        ? 'Il caricamento ha impiegato troppo tempo. Riprova senza chiudere la pagina.'
        : err?.response?.data?.error || "Errore nell'aggiornamento della persona"
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div className="person-overlay-container" onClick={onClose}>
      <div className="person-overlay-content" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Modifica Persona</h2>

        {error   && <p className="error">{error}</p>}
        {success && <p className="success">{success}</p>}

        <div className="person-overlay-body">
          <form onSubmit={handleSubmit}>
            {/* Nome --------------------------------------------------- */}
            <label>
              Nome:
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
              />
            </label>

            {/* Cognome ---------------------------------------------- */}
            <label>
              Cognome:
              <input
                type="text"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                required
              />
            </label>

            {/* Telefono --------------------------------------------- */}
            <label>
              Telefono:
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Numero di telefono"
              />
            </label>

            {/* Email ------------------------------------------------ */}
            <label>
              Email:
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Indirizzo email"
              />
            </label>

            {/* Data di nascita (max = today) ------------------------- */}
            <label>
              Data di nascita:
              <input
                type="date"
                name="data_nascita"
                value={formData.data_nascita}
                onChange={handleChange}
                max={todayStr}          /* ← blocco date future */
              />
            </label>

            {/* Sesso ------------------------------------------------- */}
            <label>
              Sesso:
              <select
                name="sesso"
                value={formData.sesso}
                onChange={handleChange}
                required
              >
                <option value="">Seleziona</option>
                <option value="M">Maschile</option>
                <option value="F">Femminile</option>
                <option value="Altro">Altro</option>
              </select>
            </label>

            {/* Upload nuova immagine -------------------------------- */}
            <label>
              Upload nuova immagine:
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isImageLoading || isSubmitting}
              />
            </label>
            {isImageLoading && <p>Preparazione immagine…</p>}
            {preview && (
              <div className="preview-container">
                <img src={preview} alt="Anteprima" />
              </div>
            )}
            {imageInfo && (
              <small>
                Immagine ottimizzata: {imageInfo.width}×{imageInfo.height}, {' '}
                {formatImageBytes(imageInfo.originalBytes)} → {formatImageBytes(imageInfo.outputBytes)}
              </small>
            )}

            {/* Bio --------------------------------------------------- */}
            <label>
              Bio (Description):
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Inserisci una breve descrizione..."
              />
            </label>

            {/* Qualifiche ------------------------------------------- */}
            <label>
              Qualifiche:
              <Select
                isMulti
                name="qualifications"
                options={qualificationOptions}
                onChange={handleQualificationSelect}
                placeholder="Seleziona una o più qualifiche..."
                isClearable
                classNamePrefix="select"
                value={qualificationOptions.filter(opt =>
                  formData.qualifications.includes(opt.value)
                )}
              />
            </label>

            <button type="submit" disabled={isImageLoading || isSubmitting}>
              {isImageLoading
                ? 'Preparazione immagine…'
                : isSubmitting
                  ? 'Salvataggio…'
                  : 'Salva Modifiche'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default PersonOverlay
